import { STANDARD_AUTH_FORMS } from '@/constants/auth'

interface ResolveFormFallbacksParams {
  /** The form currently rendered (the `?form=` value, or the default). */
  form: string
  /** `initialAuthForm ?? scene defaultForm ?? 'main'`. */
  defaultForm: string
  isLoggedIn: boolean
}

export interface ResolvedFormFallbacks {
  /** Where a visitor goes when the form they are on is not usable by them. */
  fallbackForm: string
  /** Where to land when the current `?form=` has no layout in the cinematic. */
  recoveryForm: string
}

/**
 * The two escape hatches out of a form the visitor cannot use. Both default to
 * the scene's own landing form rather than a hardcoded 'main' — a scene that
 * sets `defaultForm` need not define a `main` layout at all — with one
 * exception each, and both exceptions exist because a page can supply the
 * default itself via `initialAuthForm`:
 *
 *  - `pair` needs a session. On /pair `defaultForm` *is* 'pair', so bouncing an
 *    anonymous visitor to it is a no-op that strands them on PIN entry with no
 *    token to validate it.
 *  - Recovery must not target the form it is recovering *from*. /callback and
 *    /user/recover pass their structural form as `initialAuthForm`, so when the
 *    cinematic defines no layout for it the fallback is that same dead key and
 *    the visitor keeps an empty card.
 *
 * Main is the one guaranteed landing form, so it absorbs both.
 */
export const resolveFormFallbacks = ({
  form,
  defaultForm,
  isLoggedIn
}: ResolveFormFallbacksParams): ResolvedFormFallbacks => {
  const isPairRoute = defaultForm === STANDARD_AUTH_FORMS.PAIR

  const fallbackForm = isPairRoute && !isLoggedIn
    ? STANDARD_AUTH_FORMS.MAIN
    : defaultForm

  const recoveryForm = fallbackForm === form
    ? STANDARD_AUTH_FORMS.MAIN
    : fallbackForm

  return { fallbackForm, recoveryForm }
}

interface FormRecoveryDecision {
  /** Navigate to `recoveryForm` now. */
  shouldRecover: boolean
  /** Forms already fled, to carry into the next call. */
  recoveredForms: string[]
}

/**
 * Whether a dead form should be escaped, given the ones already escaped.
 *
 * A scene defining neither the page's structural form nor `main` is dead at
 * *both* ends of the hop — 'callback' has no layout, 'main' is missing — so
 * recovering unconditionally bounces between them forever, rewriting the URL
 * through nuqs on every pass. Recording each fled form bounds that.
 *
 * The record clears the moment the visitor lands somewhere renderable, and that
 * matters: Login, Register and Joinnus all navigate Back to a hardcoded 'main',
 * so on a scene with `defaultForm` and no `main` layout every Back is a fresh,
 * legitimate recovery. A latch that never cleared would leave the visitor on a
 * blank card from the second Back onwards.
 */
export const decideFormRecovery = (
  recoveredForms: readonly string[],
  form: string,
  isDeadForm: boolean
): FormRecoveryDecision => {
  if (!isDeadForm) {
    return { shouldRecover: false, recoveredForms: [] }
  }

  if (recoveredForms.includes(form)) {
    return { shouldRecover: false, recoveredForms: [...recoveredForms] }
  }

  return { shouldRecover: true, recoveredForms: [...recoveredForms, form] }
}
